import { jsx as _jsx } from "react/jsx-runtime";
import { reportErrorToBugsnag } from "@air/utils-error";
import noop from "lodash/noop";
import { createContext, useContext, useMemo, useRef } from "react";
import isEqual from "react-fast-compare";
import { isDevOrTestStage } from "~/swr-hooks/utils";
const defaultValue = {
    getSearchResults: ()=>[],
    subscribeToResultsChange: ()=>({
            unsubscribe: noop
        }),
    setSearchResults: noop,
    getSearchText: ()=>"",
    setSearchText: noop,
    fetchSearchResultsForRange: ()=>Promise.reject("No fetchSearchResultsForRange provided"),
    setFetchSearchResultsForRange: noop
};
export const TranscriptSearchResultsContext = /*#__PURE__*/ createContext(defaultValue);
export const TranscriptSearchResultsProvider = (param)=>{
    let { children } = param;
    const results = useRef([]);
    const subscribers = useRef([]);
    const fetchSearchResultsForRange = useRef();
    const searchTextRef = useRef("");
    const value = useMemo(()=>({
            getSearchText: ()=>searchTextRef.current,
            setSearchText: (term)=>{
                searchTextRef.current = term;
            },
            getSearchResults: ()=>results.current,
            subscribeToResultsChange: (observer)=>{
                subscribers.current.push(observer);
                return {
                    unsubscribe: ()=>{
                        subscribers.current = subscribers.current.filter((sub)=>sub !== observer);
                    }
                };
            },
            setSearchResults: (newResults)=>{
                if (isEqual(results.current, newResults)) return;
                results.current = newResults;
                subscribers.current.forEach((observer)=>{
                    observer(newResults);
                });
            },
            fetchSearchResultsForRange: (timeRange)=>{
                if (!fetchSearchResultsForRange.current) {
                    return Promise.reject("No fetchSearchResultsForRange provided");
                }
                return fetchSearchResultsForRange.current(timeRange);
            },
            setFetchSearchResultsForRange: (fetchResults)=>{
                fetchSearchResultsForRange.current = fetchResults;
            }
        }), []);
    return /*#__PURE__*/ _jsx(TranscriptSearchResultsContext.Provider, {
        value: value,
        children: children
    });
};
export const useTranscriptSearchResultsContext = ()=>{
    const context = useContext(TranscriptSearchResultsContext);
    if (context === defaultValue) {
        const error = "TranscriptSearchResultsContext must be used within a TranscriptSearchResultsProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
